import React, { useState, useEffect } from 'react';
import StandardModal, { StandardModalBody, StandardModalFooter, useModalLoading } from '../../../components/modales/StandardModal';
import { TextField, Button, Select, MenuItem, FormControl, InputLabel, Box, Typography } from '@mui/material';
import { formatCurrency } from '../../../utils/format/currency';
import axios from 'axios';
import { Loan } from '../../../models/loan';
import { LoanType } from '../../../models/loan-type';
import { useT } from '../../../i18n';

const getPeriodCount = (termInDays: number, loanTypeKindId?: number | null): number => {
    if (!Number.isFinite(termInDays) || termInDays <= 0) return 1;
    if (loanTypeKindId === 2) return Math.max(1, Math.round((termInDays / 30) * 4));
    if (loanTypeKindId === 3) return Math.max(1, Math.round(termInDays / 15));
    if (loanTypeKindId === 4) return Math.max(1, Math.round(termInDays / 30));
    return termInDays;
};

interface LoanTypeModalProps {
    open: boolean;
    onClose: () => void;
    officeId: string;
    // loanTypeId: string;
    routeId: string;
    userId: string;
    client: Loan | null;
    showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
    showLoading: () => void;
    hideLoading: () => void;
    onPaymentSuccess: () => void;
}

const LoanTypeModal: React.FC<LoanTypeModalProps> = ({
    open, onClose, client, showAlert, routeId, userId, officeId, showLoading, hideLoading, onPaymentSuccess
}) => {
    const t = useT();
  // El Cargando general queda detrás del modal: se usa el del modal estándar.
  const { loading: modalBusy, showLoading: showModalLoading, hideLoading: hideModalLoading } = useModalLoading();
    const [loanTypes, setLoanTypes] = useState<LoanType[]>([]);
    const [loanTypeId, setLoanTypeId] = useState('');
    const [termDays, setTermDays] = useState('');
    const [interestRate, setInterestRate] = useState('');
    const [loanId, setLoanId] = useState('');
    const [isWeekly, setIsWeekly] = useState(false);
    const [selectedLoanTypeKindId, setSelectedLoanTypeKindId] = useState<number | null>(null);
    const [visitDay, setVisitDay] = useState('');
    const [value, setValue] = useState('');
    const [totalValue, setTotalValue] = useState(0);
    const [installmentValue, setInstallmentValue] = useState(0);
    const [location, setLocation] = useState<string | null>(null);

    useEffect(() => {
        const fetchLoanTypes = async () => {
            try {
                const { data } = await axios.get(`/offices/${officeId}/loan-types/complete`);
                setLoanTypes(Array.isArray(data?.loanTypes) ? data.loanTypes : []);
            } catch {
                showAlert(t('loanActions.typesLoadError'), 'error');
            }
        };

        if (officeId) fetchLoanTypes();
    }, [officeId]);

    useEffect(() => {
        if (!loanTypeId || selectedLoanTypeKindId != null || loanTypes.length === 0) {
            return;
        }

        const selected = loanTypes.find((loanType) => String(loanType.id) === String(loanTypeId));
        if (!selected) {
            return;
        }

        setSelectedLoanTypeKindId(Number(selected.loanTypeKind?.id) || null);
        setIsWeekly(Number(selected.loanTypeKind?.id) === 2);
    }, [loanTypeId, selectedLoanTypeKindId, loanTypes]);

    useEffect(() => {
        const parsedValue = parseFloat(value) || 0;
        const parsedInterestRate = parseFloat(interestRate) || 0;
        const periodCount = Math.max(1, parseInt(termDays) || 1);
        const total = parsedValue + (parsedValue * parsedInterestRate / 100);
        const daily = Math.round(total / periodCount);
        setTotalValue(total);
        setInstallmentValue(daily);
    }, [value, termDays, interestRate, selectedLoanTypeKindId]);

    /** `termDays` guarda el número de cuotas del préstamo. */
    const paymentCount = parseInt(termDays) || 0;
    const selectedLoanType = loanTypes.find((c) => String(c.id) === String(loanTypeId));
    const fixedInterest = !selectedLoanType || Number(selectedLoanType.fixedInterest ?? 1) !== 0;

    useEffect(() => {
        if (open && client) {
            setLoanId(client.id?.toString() || '');
            //console.log('client constant:', client.id);
            setLoanTypeId(client.loanType?.id?.toString() || '');
            // Las cuotas y el % que ya tiene el préstamo.
            setTermDays(String(client.termDays ?? ''));
            setInterestRate(String(client.interestRate ?? client.loanType?.interestRate ?? ''));
            setSelectedLoanTypeKindId(Number(client.loanType?.loanTypeKind?.id) || null);
            setIsWeekly(client.loanType?.loanTypeKind?.id === 2);
            setValue(client.value?.toString() || '');
            setTotalValue(Number(client.totalValue) || 0);
            setInstallmentValue(Number(client.installmentValue) || 0);
            const rd = (client as Loan & { visitDay?: number | string | null }).visitDay;
            setVisitDay(
                rd === 0 || rd === '0' || (rd != null && rd !== '')
                    ? String(rd)
                    : ''
            );
        }
    }, [open, client]);



    const getCoordinates = (): Promise<string> => {
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
                showAlert(t('loanActions.geoUnsupported'), 'warning');
                return reject(new Error('Geolocation not supported'));
            }
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const lat = position.coords.latitude;
                    const lng = position.coords.longitude;
                    resolve(`${lat}, ${lng}`);
                },
                (error) => reject(error),
                { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
            );
        });
    };


    const handleSubmit = async () => {
        if (!Number.isInteger(Number(termDays)) || Number(termDays) < 1) {
            showAlert(t('lend.installmentsRequired'), 'warning');
            return;
        }
        if (!fixedInterest && (interestRate === '' || Number(interestRate) < 0)) {
            showAlert(t('lend.utilityRequired'), 'warning');
            return;
        }
        if (!loanTypeId || !value) {
            showAlert(t('loanActions.fieldsRequired'), 'warning');
            return;
        }
        if (isWeekly && (visitDay === '' || visitDay === undefined)) {
            showAlert(t('loanActions.routeDayRequired'), 'warning');
            return;
        }

        try {
            showModalLoading();

            // Obtener coordenadas si están disponibles
            let coords: string | null = null;
            try {
                coords = await getCoordinates();
            } catch {
                showAlert(t('loanActions.locationError'), 'info');
            }

            const formData = {
                loanId,
                routeId,
                loanTypeId,
                value,
                interestRate,
                userId,
                termDays: paymentCount,
                totalValue,
                installmentValue,
                location: coords,
                visitDay: isWeekly ? visitDay : null,
            };
            //console.log('Form Data:', loanId);
            const url = `/loans/${loanId}/loan-type`;
            await axios.put(url, formData);
            showAlert(t('loanActions.loanTypeOk'), 'success');
            onPaymentSuccess();
            onClose();
        } catch {
            showAlert(t('loanActions.loanTypeError'), 'error');
        } finally {
            hideModalLoading();
        }
    };


    return (
        <StandardModal
          open={open}
          onClose={onClose}
          title={t('lend.type')}
          maxWidth={500}
          loading={modalBusy}
          composed
        >
          <StandardModalBody>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 2 }}>
                <Box>
                    <FormControl fullWidth>
                        <InputLabel id="constant-modal-constant-label">{t('lend.type')}</InputLabel>
                        <Select
                            id="constant-modal-select"
                            labelId="constant-modal-constant-label"
                            value={loanTypeId}
                            label={t('lend.type')}
                            displayEmpty
                            renderValue={(selected) => {
                                if (selected === '' || selected == null) {
                                    return '\u00a0';
                                }
                                const c = loanTypes.find(
                                    (x) => String(x.id) === String(selected)
                                );
                                return c?.name ?? '';
                            }}
                            onChange={(e) => {
                                const selectedId = Number(e.target.value);
                                setLoanTypeId(selectedId.toString());

                                const selected = loanTypes.find(c => c.id === selectedId);
                                if (selected) {
                                    // Constantes de Mis Rutas: cuotas de su plazo; tipos nuevos: se conservan las del préstamo.
                                    if (Number(selected.term) > 0) setTermDays(String(getPeriodCount(Number(selected.term), Number(selected.loanTypeKind?.id))));
                                    setInterestRate(Number(selected.fixedInterest ?? 1) !== 0 ? String(selected.interestRate) : '');
                                    setSelectedLoanTypeKindId(Number(selected.loanTypeKind.id));
                                    setIsWeekly(selected.loanTypeKind.id === 2);
                                }
                            }}
                            size="small"
                        >
                            <MenuItem value="" disabled>
                                <em>{t('lend.typePlaceholder')}</em>
                            </MenuItem>
                            {loanTypes.map((loanType) => (
                                <MenuItem key={loanType.id} value={loanType.id.toString()}>
                                    {loanType.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {isWeekly && (
                    <Box>
                        <FormControl fullWidth>
                            <InputLabel id="constant-modal-route-day-label">{t('loanActions.routeDay')}</InputLabel>
                            <Select
                                id="constant-modal-route-day"
                                labelId="constant-modal-route-day-label"
                                label={t('loanActions.routeDay')}
                                value={visitDay}
                                onChange={(e) => setVisitDay(e.target.value)}
                                size="small"
                            >
                                <MenuItem value="1">{t('loanActions.monday')}</MenuItem>
                                <MenuItem value="2">{t('loanActions.tuesday')}</MenuItem>
                                <MenuItem value="3">{t('loanActions.wednesday')}</MenuItem>
                                <MenuItem value="4">{t('loanActions.thursday')}</MenuItem>
                                <MenuItem value="5">{t('loanActions.friday')}</MenuItem>
                                <MenuItem value="6">{t('loanActions.saturday')}</MenuItem>
                                <MenuItem value="0">{t('loanActions.sunday')}</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                )}

                <Box>
                    <TextField
                        label={t('lend.installments')}
                        value={termDays}
                        type="number"
                        onChange={(e) => setTermDays(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
                        inputProps={{ min: 1, max: 1000, step: 1, inputMode: 'numeric' }}
                        size="small"
                        fullWidth
                    />
                </Box>

                <Box>
                    <TextField
                        label={t('loanActions.requestedValue')}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        type="number"
                        size="small"
                        fullWidth
                    />
                </Box>

                <Box>
                    <TextField
                        label={t('lend.utility')}
                        value={interestRate}
                        onChange={(e) => {
                            if (!fixedInterest) setInterestRate(e.target.value.replace(/[^0-9]/g, '').slice(0, 4));
                        }}
                        type="number"
                        size="small"
                        required={!fixedInterest}
                        helperText={selectedLoanType ? t(fixedInterest ? 'lend.utilityFixedHelp' : 'lend.utilityFreeHelp') : undefined}
                        InputProps={{ readOnly: fixedInterest }}
                        fullWidth
                    />
                </Box>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                        gap: 2,
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: 'action.hover',
                    }}
                >
                    <Box>
                        <Typography variant="caption" color="text.secondary" component="div">{t('lend.totalToPay')}</Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{formatCurrency(totalValue)}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary" component="div">{t('lend.installmentValue')}</Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{formatCurrency(installmentValue)}</Typography>
                    </Box>
                </Box>
            </Box>


          </StandardModalBody>
          <StandardModalFooter>
            <Button variant="outlined" onClick={onClose}>{t('common.cancel')}</Button>
            <Button variant="contained" onClick={handleSubmit}>{t('common.save')}</Button>

          </StandardModalFooter>
        </StandardModal>
    );
};

export default LoanTypeModal;
